<template>
  <div class="h-full">
    <TopBar
      @go-back="goBack"
      class="mb-6"
      :title="topBarTitle"
      prevent-go-back
      :show-back-button="showShippingReceipt"
      :show-toggle="false"
    />

    <ReceiptView
      @update-receipts="onUpdateReceipts"
      :shipping-id="shippingToShow!"
      v-if="showShippingReceipt"
      :receipts="receiptViewData"
    />

    <div v-else class="flex flex-col gap-3">
      <ShippingItem
        @click="openShipping(shipping.id)"
        v-for="shipping in shippings"
        :key="shipping.id"
        :shipping="shipping"
        @release="openConfirmationModal"
      />
    </div>

    <AcceptanceModal
      :is-open="isOpen"
      title="Confirmação"
      message="Você deseja confirmar a liberação da carga?"
      @close="closeModal"
      @confirm="handleConfirmModal"
    ></AcceptanceModal>
  </div>
</template>

<script setup lang="ts">
import AcceptanceModal from '@/components/AcceptanceModal/AcceptanceModal.vue'
import type { ReceiptDetailsProps } from '@/components/ReceiptInfo/ReceiptInfo.vue'
import ShippingItem from '@/components/ShippingItem/ShippingItem.vue'
import TopBar from '@/components/TopBar/TopBar.vue'
import { showDefaultToast } from '@/helpers/toast-models'
import { ShippingReceiptStatus } from '@/services/WarrantyApiService/types'
import { computed, ref, watch, type ComputedRef } from 'vue'
import ReceiptView from '../Receipt/ReceiptView.vue'
import {
  fetchAttestToShippingVerification,
  fetchShippingQuery,
  fetchShippingReceiptsQuery
} from './query'
import { useAppStore } from '@/stores/app.store'

const isOpen = ref(false)
const shippingIdToAttest = ref<string | null>(null)
const topBarTitle = computed(() => (!shippingToShow.value ? 'ROMANEIO' : 'NOTA FISCAL'))

const appStore = useAppStore()

const { data: shippings, refetch: shippingQuery } = fetchShippingQuery()
const shippingToShow = ref<string | null>(null)

const { data: shippingReceipts, refetch: refetchShippingReceipts } =
  fetchShippingReceiptsQuery(shippingToShow)

const { refetch: attestToShippingVerification } =
  fetchAttestToShippingVerification(shippingIdToAttest)

const showShippingReceipt = computed(() => !!shippingToShow.value)

const onCompleteAllShippingReceipts = () => {
  shippingToShow.value = null

  showDefaultToast('Romaneio completo!', 'success', 3000)
}

const receiptViewData: ComputedRef<ReceiptDetailsProps[]> = computed(() => {
  return (
    shippingReceipts.value?.map((receipt) => ({
      id: receipt.id,
      receiptCode: receipt.receiptCode,
      barCode1: receipt.barCode1,
      barCode2: receipt.barCode2,
      carrier: receipt.carrier,
      cnpjDest: receipt.cnpjDest,
      totalVolume: receipt.totalVolume,
      grouping: receipt.isGrouped,
      invoiceDate: receipt.invoiceDate,
      shippingReceiptProducts: receipt.products.map((product) => ({
        id: product.id,
        productId: product.productId,
        productCode: product.productCode,
        description: product.productDescription,
        volume: product.productQuantity,
        totalChecked: product.quantityAttested || 0
      })),
      shippingReceiptCarrierTags: receipt.carrierTags.map((carrierTag) => ({
        id: carrierTag.id,
        barCode1: carrierTag.barCode1,
        barCode2: carrierTag.barCode2,
        productUnitId: carrierTag.productUnitId
      })),
      status: receipt.status
    })) || []
  )
})

const onUpdateReceipts = async () => {
  await refetchShippingReceipts()

  const allShippingReceiptCompleted = receiptViewData.value.every(
    (receipt) => receipt.status === ShippingReceiptStatus.COMPLETED
  )

  if (allShippingReceiptCompleted) {
    onCompleteAllShippingReceipts()
  }
}

const goBack = () => {
  if (shippingToShow.value) {
    shippingToShow.value = ''
  }
}

const openShipping = async (shippingId: string) => {
  shippingToShow.value = shippingId
  appStore.actions.setCurrentPage('shipping')
}

const closeModal = async () => {
  isOpen.value = false
}

const handleConfirmModal = async () => {
  await attestToShippingVerification()
  await shippingQuery()
  closeModal()
}

const openConfirmationModal = async (id: string) => {
  shippingIdToAttest.value = id
  isOpen.value = true
}

watch(showShippingReceipt, async () => {
  if (!showShippingReceipt.value) {
    await shippingQuery()
  }
})
</script>
